import React, { useState } from 'react'
import { Alert, Button, Grid, PasswordInput, Select, Stack, TextInput } from '@mantine/core'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { useActions } from 'src/state'

const US_STATES = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
    "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
    "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana",
    "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
    "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
    "New Hampshire", "New Jersey", "New Mexico", "New York",
    "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
    "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
    "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
    "West Virginia", "Wisconsin", "Wyoming",
]

const validationSchema = yup.object({
    email: yup.string().required('Email is required').email('Invalid email address'),
    first_name: yup.string().required('First name is required'),
    last_name: yup.string().required('Last name is required'),
    city: yup.string().required('City is required'),
    state: yup.string().required('State is required'),
    password: yup.string().required('Password is required').min(8, 'Password must be at least 8 characters').matches(/[a-zA-Z]/, 'Password must contain at least one letter'),
    verify_password: yup.string().required('Please confirm your password').oneOf([yup.ref('password')], 'Passwords do not match'),
})

const TalentSignUpForm: React.FC = () => {

    const signUpAction = useActions().signUpAction
    const [formError, setFormError] = useState<string | null>(null)

    const formik = useFormik({
        initialValues: {
            email: '',
            first_name: '',
            last_name: '',
            city: '',
            state: '',
            password: '',
            verify_password: '',
        },
        validationSchema,
        validateOnBlur: true,
        validateOnChange: true,
        onSubmit: async ({ verify_password: _, ...values }, { setErrors }) => {
            setFormError(null)
            const res = await signUpAction({ ...values, signup_mode: 'talent' })
            if (!res.success) {
                if (res.errors.form) setFormError(res.errors.form)
                if (Object.keys(res.errors.fields).length) setErrors(res.errors.fields)
                return
            }
            window.location.assign('/verify-email')
        },
    })

    return (
        <form onSubmit={formik.handleSubmit}>
            <Stack gap="sm">

                {formError && (
                    <Alert color="red">{formError}</Alert>
                )}

                <TextInput
                    type="email"
                    label="Email"
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && formik.errors.email}
                    autoComplete="email"
                    radius="md"
                />

                <Grid>
                    <Grid.Col span={6}>
                        <TextInput
                            type="text"
                            label="First Name"
                            name="first_name"
                            value={formik.values.first_name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.first_name && formik.errors.first_name}
                            radius="md"
                        />
                    </Grid.Col>
                    <Grid.Col span={6}>
                        <TextInput
                            type="text"
                            label="Last Name"
                            name="last_name"
                            value={formik.values.last_name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.last_name && formik.errors.last_name}
                            radius="md"
                        />
                    </Grid.Col>
                </Grid>

                <Grid>
                    <Grid.Col span={6}>
                        <TextInput
                            type="text"
                            label="City"
                            name="city"
                            value={formik.values.city}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.city && formik.errors.city}
                            radius="md"
                        />
                    </Grid.Col>
                    <Grid.Col span={6}>
                        <Select
                            label="State"
                            data={US_STATES}
                            value={formik.values.state}
                            onChange={(value) => formik.setFieldValue('state', value ?? '')}
                            onBlur={() => formik.setFieldTouched('state', true)}
                            error={formik.touched.state && formik.errors.state}
                            searchable
                            radius="md"
                        />
                    </Grid.Col>
                </Grid>

                <PasswordInput
                    label="Password"
                    name="password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.password && formik.errors.password}
                    autoComplete="new-password"
                    radius="md"
                />

                <PasswordInput
                    label="Confirm Password"
                    name="verify_password"
                    value={formik.values.verify_password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.verify_password && formik.errors.verify_password}
                    autoComplete="new-password"
                    radius="md"
                />

                <Button
                    type="submit"
                    fullWidth
                    loading={formik.isSubmitting}
                    disabled={!formik.isValid || formik.isSubmitting}
                    mt="sm"
                    radius="xl"
                    size="md"
                >
                    Sign Up
                </Button>

            </Stack>
        </form>
    )
}

export default TalentSignUpForm
